import * as React from 'react';
import {
    Box,
    Button,
    Chip,
    Checkbox,
    Drawer,
    FormControlLabel,
    MenuItem,
    Stack,
    Tab,
    Tabs,
    TextField,
    Typography,
} from '@mui/material';
import HelpOutlineIcon from '@mui/icons-material/HelpOutline';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import EventBusyIcon from '@mui/icons-material/EventBusy';
import HistoryIcon from '@mui/icons-material/History';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import PersonIcon from '@mui/icons-material/Person';
import PersonAddIcon from '@mui/icons-material/PersonAdd';
import ToggleOnIcon from '@mui/icons-material/ToggleOn';
import FilterListIcon from '@mui/icons-material/FilterList';
import SecurityIcon from '@mui/icons-material/Security';
import { useRecordContext, useStore, useTranslate } from 'react-admin';
import { useCanAccess } from '@react-admin/ra-rbac';
import { useLocation, useSearchParams } from 'react-router-dom';
import BulkMergePage from '../user/BulkMergePage';
import MergeHistoryPage from '../user/MergeHistoryPage';
import MergeReportPage from '../user/MergeReportPage';
import { MERGE_RESOURCES, MERGE_VIEWS } from '../user/mergeNavigation';

const SchoolMergeTab = () => {
    const school = useRecordContext();
    const translate = useTranslate();
    const { canAccess, isPending } = useCanAccess({
        action: 'read',
        resource: 'merge.workspace',
    });
    const { pathname } = useLocation();
    const [params, setParams] = useSearchParams();
    const [busy, setBusy] = React.useState(false);
    const [helpOpen, setHelpOpen] = React.useState(false);
    const [autoOpenHelp, setAutoOpenHelp] = useStore<boolean>(
        'mergeHelpAutoOpen',
        true
    );
    const schoolType = Number(school?.public?.type);
    const isUpperSecondarySchool =
        (schoolType >= 131 && schoolType <= 139) ||
        schoolType === 144 ||
        schoolType === 147;
    const resource =
        MERGE_RESOURCES.find(value => value === params.get('resource')) ||
        (isUpperSecondarySchool ? 'student' : 'pupil');
    const view =
        MERGE_VIEWS.find(value => value === params.get('view')) || 'export';
    const isSectionActive =
        !isPending &&
        canAccess &&
        pathname.endsWith('/yhdistaminen') &&
        Boolean(school?.id);
    const wasSectionActive = React.useRef(false);

    React.useEffect(() => {
        if (isSectionActive && !wasSectionActive.current && autoOpenHelp)
            setHelpOpen(true);
        wasSectionActive.current = isSectionActive;
    }, [autoOpenHelp, isSectionActive]);

    // FormTab also renders inactive panels; do not fetch merge data there.
    if (!isSectionActive) return null;

    const change = (key: string, value: string) => {
        const next = new URLSearchParams(params);
        next.set(key, value);
        setParams(next);
    };
    const context = {
        school: String(school.id),
        resource,
        onBusyChange: setBusy,
    };
    const helpSections = [
        {
            id: 'overview',
            title: 'merge.workspace.title',
            paragraphs: [
                'merge.bulk.report_workflow',
                'merge.bulk.report_workflow_note',
            ],
        },
        {
            id: 'export',
            title: 'merge.workspace.export',
            paragraphs: [
                'merge.bulk.report_note',
                'merge.bulk.export_exclusion_help',
                'merge.bulk.account_details_help',
                'merge.bulk.sort_export_help',
                'merge.bulk.search_help',
                'merge.bulk.filters_help',
                'merge.bulk.identifier_domain_help',
            ],
        },
        {
            id: 'merge',
            title: 'merge.workspace.merge',
            paragraphs: [
                'merge.bulk.sort_merge_help',
                'merge.bulk.select_last_export_help',
                'merge.bulk.account_details_help',
                'merge.bulk.filters_help',
                'merge.bulk.sort_merge_reminder',
            ],
        },
        {
            id: 'report',
            title: 'merge.workspace.report',
            paragraphs: [
                'merge.history.report_help',
                'merge.history.report_note',
                'merge.history.mpass_help',
                'merge.history.report_missing_audit',
            ],
        },
        {
            id: 'history',
            title: 'merge.workspace.history',
            paragraphs: ['merge.history.description', 'merge.history.mpass_help'],
        },
    ];

    return (
        <Box
            width="100%"
            onKeyDown={event => {
                if (
                    event.key === 'Enter' &&
                    event.target instanceof HTMLInputElement
                )
                    event.preventDefault();
            }}
        >
            <Stack
                direction="row"
                alignItems="center"
                justifyContent="space-between"
                spacing={2}
            >
                <TextField
                    select
                    size="small"
                    label={translate('merge.workspace.role')}
                    value={resource}
                    disabled={busy}
                    onChange={event => change('resource', event.target.value)}
                    sx={{ minWidth: 240, my: 2 }}
                >
                    {MERGE_RESOURCES.map(value => (
                        <MenuItem key={value} value={value}>
                            {translate(`resources.${value}.name`, {
                                smart_count: 2,
                            })}
                        </MenuItem>
                    ))}
                </TextField>
                <Button
                    type="button"
                    startIcon={<HelpOutlineIcon />}
                    onClick={() => setHelpOpen(true)}
                    aria-haspopup="dialog"
                    aria-expanded={helpOpen}
                >
                    {translate('merge.workspace.help')}
                </Button>
            </Stack>
            <Drawer
                anchor="right"
                open={helpOpen}
                onClose={() => setHelpOpen(false)}
                PaperProps={{
                    role: 'dialog',
                    'aria-modal': true,
                    'aria-labelledby': 'merge-help-title',
                    sx: { width: 480, maxWidth: '100vw' },
                }}
            >
                <Box p={3}>
                    <Stack
                        direction="row"
                        alignItems="center"
                        justifyContent="space-between"
                        spacing={2}
                        mb={3}
                    >
                        <Typography id="merge-help-title" variant="h6">
                            {translate('merge.workspace.help')}
                        </Typography>
                        <Button type="button" onClick={() => setHelpOpen(false)}>
                            {translate('ra.action.close')}
                        </Button>
                    </Stack>
                    <FormControlLabel
                        control={
                            <Checkbox
                                checked={autoOpenHelp}
                                onChange={event =>
                                    setAutoOpenHelp(event.target.checked)
                                }
                            />
                        }
                        label={translate(
                            'merge.workspace.help_auto_open'
                        )}
                        sx={{ mb: 2 }}
                    />
                    {helpSections.map(section => (
                        <Box
                            component="details"
                            key={`${section.id}:${view}:${helpOpen}`}
                            open={section.id === 'overview'}
                            sx={{
                                mb: 2,
                                pb: 2,
                                borderBottom: 1,
                                borderColor: 'divider',
                            }}
                        >
                            <Box
                                component="summary"
                                sx={{ cursor: 'pointer' }}
                            >
                                <Typography
                                    component="span"
                                    variant="subtitle1"
                                    fontWeight="bold"
                                >
                                    {translate(section.title)}
                                </Typography>
                                <Typography
                                    variant="body2"
                                    sx={{ mt: 0.5, color: 'text.secondary' }}
                                >
                                    {translate(
                                        `merge.workspace.help_${section.id}`
                                    )}
                                </Typography>
                            </Box>
                            <Box sx={{ mt: 2 }}>
                                {section.id === 'export' ? (
                                    <Stack spacing={2}>
                                        <Box
                                            sx={{
                                                p: 1.5,
                                                borderRadius: 1.5,
                                                border: '1px solid',
                                                borderColor: 'info.main',
                                                backgroundColor: 'action.hover',
                                            }}
                                        >
                                            <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 0.5 }}>
                                                <SecurityIcon fontSize="small" color="info" />
                                                <Typography variant="subtitle2" fontWeight="bold">
                                                    {translate('merge.workspace.help_export_privacy_title')}
                                                </Typography>
                                            </Stack>
                                            <Typography variant="body2">{translate('merge.bulk.report_note')}</Typography>
                                        </Box>

                                        <Box
                                            sx={{
                                                p: 1.5,
                                                borderRadius: 1.5,
                                                border: '1px solid',
                                                borderColor: 'divider',
                                            }}
                                        >
                                            <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 1 }}>
                                                <ToggleOnIcon fontSize="small" color="primary" />
                                                <Typography variant="subtitle2" fontWeight="bold">
                                                    {translate('merge.workspace.help_export_groups_title')}
                                                </Typography>
                                            </Stack>
                                            <Stack spacing={1} sx={{ mb: 1.5 }}>
                                                <Stack direction="row" spacing={1} alignItems="center">
                                                    <Chip size="small" color="success" variant="outlined" label="ON" />
                                                    <Typography variant="body2">
                                                        {translate('merge.workspace.help_legend_switch_included')}
                                                    </Typography>
                                                </Stack>
                                                <Stack direction="row" spacing={1} alignItems="center">
                                                    <Chip size="small" variant="outlined" label="OFF" sx={{ borderStyle: 'dashed' }} />
                                                    <Typography variant="body2">
                                                        {translate('merge.workspace.help_legend_switch_excluded')}
                                                    </Typography>
                                                </Stack>
                                                <Stack direction="row" spacing={1} alignItems="center">
                                                    <Chip size="small" color="error" label="MPASS" />
                                                    <Typography variant="body2">
                                                        {translate('merge.workspace.help_legend_mpass_locked')}
                                                    </Typography>
                                                </Stack>
                                            </Stack>
                                            <Typography variant="body2" sx={{ whiteSpace: 'pre-line' }}>
                                                {translate('merge.bulk.export_exclusion_help')}
                                            </Typography>
                                        </Box>

                                        <Box
                                            sx={{
                                                p: 1.5,
                                                borderRadius: 1.5,
                                                border: '1px solid',
                                                borderColor: 'divider',
                                            }}
                                        >
                                            <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 1 }}>
                                                <WarningAmberIcon fontSize="small" color="warning" />
                                                <Typography variant="subtitle2" fontWeight="bold">
                                                    {translate('merge.workspace.help_export_indicators_title')}
                                                </Typography>
                                            </Stack>
                                            <Stack spacing={1} sx={{ mb: 1.5 }}>
                                                <Stack direction="row" spacing={1} alignItems="center">
                                                    <Chip
                                                        size="small"
                                                        color="success"
                                                        variant="filled"
                                                        label={translate('merge.workspace.license_legend_overlapping_sample')}
                                                    />
                                                    <Typography variant="body2">
                                                        {translate('merge.workspace.license_legend_overlapping')}
                                                    </Typography>
                                                </Stack>
                                                <Stack direction="row" spacing={1} alignItems="center">
                                                    <Chip
                                                        size="small"
                                                        color="success"
                                                        variant="outlined"
                                                        label={translate('merge.bulk.license_category_school')}
                                                    />
                                                    <Typography variant="body2">
                                                        {translate('merge.bulk.license_category_school_help')}
                                                    </Typography>
                                                </Stack>
                                                <Stack direction="row" spacing={1} alignItems="center">
                                                    <Chip
                                                        size="small"
                                                        color="info"
                                                        variant="outlined"
                                                        label={translate('merge.bulk.license_category_other_school')}
                                                    />
                                                    <Typography variant="body2">
                                                        {translate('merge.bulk.license_category_other_school_help')}
                                                    </Typography>
                                                </Stack>
                                                <Stack direction="row" spacing={1} alignItems="center">
                                                    <Chip
                                                        size="small"
                                                        color="secondary"
                                                        variant="outlined"
                                                        label={translate('merge.bulk.license_category_own')}
                                                    />
                                                    <Typography variant="body2">
                                                        {translate('merge.bulk.license_category_own_help')}
                                                    </Typography>
                                                </Stack>
                                                <Stack direction="row" spacing={1} alignItems="center">
                                                    <Chip
                                                        size="small"
                                                        color="info"
                                                        label={translate('merge.bulk.overlapping_group')}
                                                    />
                                                    <Typography variant="body2">
                                                        {translate('merge.bulk.overlapping_groups', { count: '2+' })}
                                                    </Typography>
                                                </Stack>
                                                <Stack direction="row" spacing={1} alignItems="center">
                                                    <Chip
                                                        size="small"
                                                        color="warning"
                                                        icon={<WarningAmberIcon fontSize="small" />}
                                                        label={translate('merge.bulk.account_created')}
                                                    />
                                                    <Typography variant="body2">
                                                        {translate('merge.workspace.help_legend_created_warning')}
                                                    </Typography>
                                                </Stack>
                                            </Stack>
                                            <Typography variant="body2">{translate('merge.bulk.account_details_help')}</Typography>
                                        </Box>

                                        <Box
                                            sx={{
                                                p: 1.5,
                                                borderRadius: 1.5,
                                                border: '1px solid',
                                                borderColor: 'divider',
                                            }}
                                        >
                                            <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 1 }}>
                                                <FilterListIcon fontSize="small" color="action" />
                                                <Typography variant="subtitle2" fontWeight="bold">
                                                    {translate('merge.workspace.help_export_sort_title')}
                                                </Typography>
                                            </Stack>
                                            <Stack spacing={1}>
                                                <Typography variant="body2">{translate('merge.bulk.sort_export_help')}</Typography>
                                                <Typography variant="body2">{translate('merge.bulk.search_help')}</Typography>
                                                <Typography variant="body2">{translate('merge.bulk.filters_help')}</Typography>
                                                <Typography variant="body2">{translate('merge.bulk.identifier_domain_help')}</Typography>
                                            </Stack>
                                        </Box>
                                    </Stack>
                                ) : section.id === 'merge' ? (
                                    <Stack spacing={2}>
                                        <Box
                                            sx={{
                                                p: 1.5,
                                                borderRadius: 1.5,
                                                border: '1px solid',
                                                borderColor: 'warning.main',
                                                backgroundColor: 'action.hover',
                                            }}
                                        >
                                            <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 0.5 }}>
                                                <WarningAmberIcon fontSize="small" color="warning" />
                                                <Typography variant="subtitle2" fontWeight="bold">
                                                    {translate('merge.workspace.help_merge_reminder_title')}
                                                </Typography>
                                            </Stack>
                                            <Typography variant="body2" fontWeight={500}>
                                                {translate('merge.bulk.sort_merge_reminder')}
                                            </Typography>
                                        </Box>

                                        <Box
                                            sx={{
                                                p: 1.5,
                                                borderRadius: 1.5,
                                                border: '1px solid',
                                                borderColor: 'divider',
                                            }}
                                        >
                                            <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 1 }}>
                                                <HistoryIcon fontSize="small" color="primary" />
                                                <Typography variant="subtitle2" fontWeight="bold">
                                                    {translate('merge.workspace.help_merge_tools_title')}
                                                </Typography>
                                            </Stack>
                                            <Stack spacing={1}>
                                                <Stack direction="row" spacing={1} alignItems="flex-start">
                                                    <Chip
                                                        size="small"
                                                        icon={<HistoryIcon fontSize="small" />}
                                                        variant="outlined"
                                                        label={translate('merge.bulk.select_last_export')}
                                                    />
                                                </Stack>
                                                <Typography variant="body2">{translate('merge.bulk.select_last_export_help')}</Typography>
                                                <Stack direction="row" spacing={1} alignItems="center" sx={{ pt: 0.5 }}>
                                                    <Chip size="small" variant="outlined" label={translate('merge.bulk.select_page')} />
                                                    <Chip size="small" variant="outlined" label={translate('merge.bulk.clear_all_selection')} />
                                                </Stack>
                                            </Stack>
                                        </Box>

                                        <Box
                                            sx={{
                                                p: 1.5,
                                                borderRadius: 1.5,
                                                border: '1px solid',
                                                borderColor: 'divider',
                                            }}
                                        >
                                            <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 1 }}>
                                                <PersonIcon fontSize="small" color="primary" />
                                                <Typography variant="subtitle2" fontWeight="bold">
                                                    {translate('merge.workspace.help_merge_roles_title')}
                                                </Typography>
                                            </Stack>
                                            <Stack spacing={1} sx={{ mb: 1.5 }}>
                                                <Stack direction="row" spacing={1} alignItems="center">
                                                    <PersonIcon color="primary" fontSize="small" />
                                                    <Typography variant="body2" fontWeight={600}>
                                                        {translate('merge.workspace.help_legend_primary')}
                                                    </Typography>
                                                </Stack>
                                                <Stack direction="row" spacing={1} alignItems="center">
                                                    <PersonAddIcon color="action" fontSize="small" />
                                                    <Typography variant="body2">
                                                        {translate('merge.workspace.help_legend_source')}
                                                    </Typography>
                                                </Stack>
                                            </Stack>
                                            <Typography variant="body2">{translate('merge.bulk.sort_merge_help')}</Typography>
                                        </Box>

                                        <Box
                                            sx={{
                                                p: 1.5,
                                                borderRadius: 1.5,
                                                border: '1px solid',
                                                borderColor: 'divider',
                                            }}
                                        >
                                            <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 1 }}>
                                                <WarningAmberIcon fontSize="small" color="warning" />
                                                <Typography variant="subtitle2" fontWeight="bold">
                                                    {translate('merge.workspace.help_export_indicators_title')}
                                                </Typography>
                                            </Stack>
                                            <Stack spacing={1} sx={{ mb: 1.5 }}>
                                                <Stack direction="row" spacing={1} alignItems="center">
                                                    <Chip
                                                        size="small"
                                                        color="success"
                                                        variant="filled"
                                                        label={translate('merge.workspace.license_legend_overlapping_sample')}
                                                    />
                                                    <Typography variant="body2">
                                                        {translate('merge.workspace.license_legend_overlapping')}
                                                    </Typography>
                                                </Stack>
                                                <Stack direction="row" spacing={1} alignItems="center">
                                                    <Chip
                                                        size="small"
                                                        color="success"
                                                        variant="outlined"
                                                        label={translate('merge.bulk.license_category_school')}
                                                    />
                                                    <Typography variant="body2">
                                                        {translate('merge.bulk.license_category_school_help')}
                                                    </Typography>
                                                </Stack>
                                                <Stack direction="row" spacing={1} alignItems="center">
                                                    <Chip
                                                        size="small"
                                                        color="info"
                                                        variant="outlined"
                                                        label={translate('merge.bulk.license_category_other_school')}
                                                    />
                                                    <Typography variant="body2">
                                                        {translate('merge.bulk.license_category_other_school_help')}
                                                    </Typography>
                                                </Stack>
                                                <Stack direction="row" spacing={1} alignItems="center">
                                                    <Chip
                                                        size="small"
                                                        color="secondary"
                                                        variant="outlined"
                                                        label={translate('merge.bulk.license_category_own')}
                                                    />
                                                    <Typography variant="body2">
                                                        {translate('merge.bulk.license_category_own_help')}
                                                    </Typography>
                                                </Stack>
                                                <Stack direction="row" spacing={1} alignItems="center">
                                                    <Chip
                                                        size="small"
                                                        color="info"
                                                        label={translate('merge.bulk.overlapping_group')}
                                                    />
                                                    <Typography variant="body2">
                                                        {translate('merge.bulk.overlapping_groups', { count: '2+' })}
                                                    </Typography>
                                                </Stack>
                                                <Stack direction="row" spacing={1} alignItems="center">
                                                    <Chip
                                                        size="small"
                                                        color="warning"
                                                        icon={<WarningAmberIcon fontSize="small" />}
                                                        label={translate('merge.bulk.account_created')}
                                                    />
                                                    <Typography variant="body2">
                                                        {translate('merge.workspace.help_legend_created_warning')}
                                                    </Typography>
                                                </Stack>
                                            </Stack>
                                            <Typography variant="body2" sx={{ mb: 1 }}>{translate('merge.bulk.account_details_help')}</Typography>
                                            <Typography variant="body2">{translate('merge.bulk.filters_help')}</Typography>
                                        </Box>
                                    </Stack>
                                ) : (
                                    section.paragraphs.map(key => (
                                        <Typography
                                            key={key}
                                            variant="body2"
                                            sx={{ mb: 1.5, whiteSpace: 'pre-line' }}
                                        >
                                            {translate(key)}
                                        </Typography>
                                    ))
                                )}
                                {section.id === 'overview' && (
                                    <Box
                                        role="group"
                                        aria-label={translate(
                                            'merge.workspace.license_legend_title'
                                        )}
                                        sx={{ mt: 2 }}
                                    >
                                        <Typography
                                            variant="subtitle2"
                                            sx={{ mb: 1 }}
                                        >
                                            {translate(
                                                'merge.workspace.license_legend_title'
                                            )}
                                        </Typography>
                                        <Stack spacing={1}>
                                            <Stack
                                                direction="row"
                                                alignItems="center"
                                                spacing={1}
                                            >
                                                <CheckCircleIcon
                                                    fontSize="small"
                                                    color="success"
                                                />
                                                <Typography variant="body2">
                                                    {translate(
                                                        'merge.workspace.license_legend_valid'
                                                    )}
                                                </Typography>
                                            </Stack>
                                            <Stack
                                                direction="row"
                                                alignItems="center"
                                                spacing={1}
                                            >
                                                <EventBusyIcon
                                                    fontSize="small"
                                                    color="disabled"
                                                />
                                                <Typography variant="body2">
                                                    {translate(
                                                        'merge.workspace.license_legend_not_valid'
                                                    )}
                                                </Typography>
                                            </Stack>
                                            <Stack
                                                direction="row"
                                                alignItems="center"
                                                spacing={1}
                                            >
                                                <HistoryIcon fontSize="small" />
                                                <Typography variant="body2">
                                                    {translate(
                                                        'merge.workspace.license_legend_extended'
                                                    )}
                                                </Typography>
                                            </Stack>
                                            <Stack
                                                direction="row"
                                                alignItems="center"
                                                spacing={1}
                                            >
                                                <Chip
                                                    size="small"
                                                    color="success"
                                                    variant="filled"
                                                    label={translate(
                                                        'merge.workspace.license_legend_overlapping_sample'
                                                    )}
                                                />
                                                <Typography variant="body2">
                                                    {translate(
                                                        'merge.workspace.license_legend_overlapping'
                                                    )}
                                                </Typography>
                                            </Stack>
                                        </Stack>
                                    </Box>
                                )}
                            </Box>
                        </Box>
                    ))}
                </Box>
            </Drawer>
            <Tabs
                value={view}
                onChange={(_, value) => change('view', value)}
                variant="scrollable"
                scrollButtons="auto"
                aria-label={translate('merge.workspace.title')}
                sx={{ mb: 2 }}
            >
                {MERGE_VIEWS.map(value => (
                    <Tab
                        key={value}
                        value={value}
                        label={translate(`merge.workspace.${value}`)}
                        disabled={busy}
                        id={`merge-tab-${value}`}
                        aria-controls={`merge-panel-${value}`}
                    />
                ))}
            </Tabs>
            <Box
                role="tabpanel"
                id={`merge-panel-${view}`}
                aria-labelledby={`merge-tab-${view}`}
            >
                {(view === 'export' || view === 'merge') && (
                    <BulkMergePage
                        key={`${school.id}:${resource}`}
                        {...context}
                        mode={view}
                    />
                )}
                {view === 'report' && (
                    <MergeReportPage
                        key={`${school.id}:${resource}`}
                        {...context}
                    />
                )}
                {view === 'history' && (
                    <MergeHistoryPage
                        key={`${school.id}:${resource}`}
                        {...context}
                    />
                )}
            </Box>
        </Box>
    );
};

export default SchoolMergeTab;
